import { useState } from "react";
import { Button } from "@common/index";
import { ComponentColor } from "@common/types";
import { useAcceptUi, type AcceptUiCapture } from "@/hooks/useAcceptUi";
import { authNetConfig } from "@/config/paymentConfig";

// Drydock harness for the Authorize.Net AcceptUI hosted-lightbox capture step (US152 / E1-S4). It
// proves, in isolation, that the PUBLIC client key + environment are wired correctly and that the
// vendor-hosted form can turn a card into an opaque nonce WITHOUT going through
// cart/checkout/auth. The card is entered entirely inside Authorize.Net's iframe — this page only
// ever sees the nonce and vendor-masked display data. Capture does not charge — approve/decline
// happens later when the nonce is charged by the API. Run over the HTTPS dev server and use an
// Authorize.Net SANDBOX test card (e.g. Visa 4111 1111 1111 1111, any future expiry, CVV 123).
export const PaymentTokenization = () => {
    const [capture, setCapture] = useState<AcceptUiCapture | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { isConfigured, openLightbox } = useAcceptUi({
        onCapture: (next) => {
            setCapture(next);
            setError(null);
        },
        onError: (message) => setError(message),
    });

    // Cancelling a re-open must not destroy a still-valid capture — same semantics as the
    // checkout PaymentForm, so this panel exercises the real behaviour.
    const handleOpen = () => {
        setError(null);
        openLightbox();
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl">
            <h2 className="text-xl font-bold mb-1">
                Authorize.Net Hosted Card Capture (AcceptUI)
            </h2>
            <p className="text-sm mb-4">
                Environment: <b>{authNetConfig.environment}</b> · Configured:{" "}
                <b>{isConfigured ? "yes" : "no (set VITE_AUTHNET_* env vars)"}</b>
            </p>
            <p className="text-sm mb-4">
                Opens Authorize.Net&apos;s hosted lightbox — the card fields
                live inside the vendor iframe, so no PAN/CVV ever touches this
                app. Submitting the form hands back only the opaque nonce
                below.
            </p>

            <Button
                onClick={handleOpen}
                color={ComponentColor.Primary}
                disabled={!isConfigured}
            >
                Open payment form
            </Button>

            {capture && (
                <div className="mt-4 border-t pt-4">
                    <h3 className="font-semibold text-success mb-1">
                        ✓ Nonce received (vendorPaymentToken)
                    </h3>
                    <textarea
                        readOnly
                        className="textarea textarea-bordered w-full text-xs font-mono"
                        rows={3}
                        value={capture.token}
                    />
                    <p className="text-xs mt-1">
                        Masked card: <b>{capture.maskedCard || "n/a"}</b> ·
                        Expiry: <b>{capture.expDate || "n/a"}</b>
                    </p>
                    <p className="text-xs mt-1">
                        Single-use, expires in ~15 min. This is the exact value
                        the checkout sends to <code>POST /orders</code>.
                    </p>
                </div>
            )}

            {error && (
                <div className="mt-4 border-t pt-4">
                    <h3 className="font-semibold text-error mb-1">
                        ✗ Card capture failed
                    </h3>
                    <p className="text-sm">{error}</p>
                </div>
            )}
        </div>
    );
};
